/**
 * Debug Trace Key Management
 *
 * Provides a developer debugging tool that allows tracking all operations
 * (frontend → backend → async jobs) through a custom correlation key.
 *
 * Usage:
 *   Navigate to app with ?debug_trace_key=yourname-issue-description
 *   All operations will be tagged with this key in Sentry, logs, and traces.
 *
 * Clear:
 *   Navigate to ?debug_trace_key=clear or close the browser tab
 */

const STORAGE_KEY = 'debug_trace_key';
const URL_PARAM_NAME = 'debug_trace_key';
const MAX_LENGTH = 128;
const ALLOWED_PATTERN = /^[a-zA-Z0-9_-]+$/;

let currentKey: string | null = null;

/**
 * Validates a debug trace key.
 * Max 128 characters, alphanumeric plus dash/underscore only.
 */
export function validateDebugTraceKey(key: string): boolean {
  if (!key || typeof key !== 'string') {
    return false;
  }
  if (key.length > MAX_LENGTH) {
    return false;
  }
  return ALLOWED_PATTERN.test(key);
}

/**
 * Gets the current debug trace key.
 * Returns null if not set.
 */
export function getDebugTraceKey(): string | null {
  return currentKey;
}

/**
 * Sets the debug trace key and persists to sessionStorage.
 */
export function setDebugTraceKey(key: string): void {
  if (!validateDebugTraceKey(key)) {
    return;
  }
  currentKey = key;
  try {
    sessionStorage.setItem(STORAGE_KEY, key);
  } catch (e) {
    // Failed to persist to sessionStorage
  }
}

/**
 * Clears the debug trace key from memory and sessionStorage.
 */
export function clearDebugTraceKey(): void {
  currentKey = null;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    // Failed to clear from sessionStorage
  }
}

/**
 * Initializes the debug trace key from URL parameter or sessionStorage.
 * Should be called early in application bootstrap.
 */
export function initializeDebugTraceKey(): void {
  try {
    // Check if window.location.search is available
    if (
      typeof window === 'undefined' ||
      !window.location ||
      typeof window.location.search === 'undefined'
    ) {
      // No location available, just try to load from sessionStorage
      const storedKey = sessionStorage.getItem(STORAGE_KEY);
      if (storedKey && validateDebugTraceKey(storedKey)) {
        currentKey = storedKey;
      }
      return;
    }

    // Check URL parameter first
    const urlParams = new URLSearchParams(window.location.search);
    const urlKey = urlParams.get(URL_PARAM_NAME);

    if (urlKey) {
      if (urlKey === 'clear') {
        clearDebugTraceKey();
        return;
      }
      if (validateDebugTraceKey(urlKey)) {
        setDebugTraceKey(urlKey);
        return;
      }
    }

    // Fall back to sessionStorage
    const storedKey = sessionStorage.getItem(STORAGE_KEY);
    if (storedKey && validateDebugTraceKey(storedKey)) {
      currentKey = storedKey;
    }
  } catch (e) {
    // Initialization failed
  }
}
